/**
 * PasoDatosGenerales — Paso 1 del IE. Edita los 12 campos de datosGenerales.
 */
import type { IeDatosGenerales } from "../../types.js";
import { LabeledField, LabeledNumber, LabeledDate } from "./fields.js";
import { OT_LABEL } from "../paso-datos-generales-helpers.js";

export function PasoDatosGenerales(props: {
  value: IeDatosGenerales;
  onChange: (next: IeDatosGenerales) => void;
  disabled?: boolean;
  /** Rótulo del campo `supervisor`. IE usa "Evaluador"; IS usa "Supervisor". */
  supervisorLabel?: string;
}) {
  const { value, onChange, disabled, supervisorLabel = "Evaluador" } = props;

  function set<K extends keyof IeDatosGenerales>(key: K, v: IeDatosGenerales[K]) {
    onChange({ ...value, [key]: v });
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-x-8 sm:gap-y-8">
      {/* REQ-7a: cliente is always disabled — pre-filled from the informe, not editable. */}
      <LabeledField
        id="dg-cliente"
        label="Cliente"
        value={value.cliente}
        onChange={(v) => set("cliente", v)}
        disabled
        placeholder="—"
        hint="Nombre del cliente (automático desde el informe)."
      />
      <LabeledField
        id="dg-atencion"
        label="Atención"
        value={value.atencion}
        onChange={(v) => set("atencion", v)}
        disabled={disabled}
        placeholder="Ing. Hugo Sánchez"
        hint="A quién va dirigido el informe."
      />
      {/* `supervisor`: automático = técnico conectado, no editable. El rótulo
          varía por tipo de informe (Evaluador en IE, Supervisor en IS). */}
      <LabeledField
        id="dg-supervisor"
        label={supervisorLabel}
        value={value.supervisor}
        onChange={(v) => set("supervisor", v)}
        disabled
        placeholder="—"
        hint="Técnico que crea el informe (automático)."
      />
      {/* Cargo: pre-cargado con DEFAULT_CARGO en el payload, editable. Sale en el PDF (cierre). */}
      <LabeledField
        id="dg-cargo"
        label="Cargo"
        value={value.cargo}
        onChange={(v) => set("cargo", v)}
        disabled={disabled}
        placeholder="Supervisor de equipos y servicios"
        hint="Cargo del supervisor; aparece junto a la firma en el PDF."
      />
      <LabeledDate
        id="dg-fecha-inicio"
        label="Fecha de inicio"
        value={value.fechaInicio}
        onChange={(v) => set("fechaInicio", v)}
        disabled={disabled}
        hint="Cuándo empezó el trabajo."
      />
      <LabeledDate
        id="dg-fecha-cierre"
        label="Fecha de cierre"
        value={value.fechaCierre}
        onChange={(v) => set("fechaCierre", v)}
        disabled={disabled}
        hint="Cuándo terminó el trabajo."
      />
      <LabeledField
        id="dg-referencia"
        label="Referencia"
        value={value.referencia}
        onChange={(v) => set("referencia", v)}
        disabled={disabled}
        placeholder="Overhaul de sistema…"
        hint="Nombre del servicio (el mismo que en la orden de compra)."
      />
      <LabeledField
        id="dg-lugar"
        label="Lugar"
        value={value.lugar}
        onChange={(v) => set("lugar", v)}
        disabled={disabled}
        placeholder="Trackshop…"
        hint="Dónde se realizó la evaluación (detallado)."
      />
      <LabeledField
        id="dg-oc-cliente"
        label="OC Cliente"
        value={value.ocCliente}
        onChange={(v) => set("ocCliente", v)}
        disabled={disabled}
        placeholder="N° orden de compra"
        hint="Orden de compra del cliente."
      />
      {/* REQ-12: label changed from "OT MPSA" to OT_LABEL ("OT"). Key otMpsa preserved. */}
      <LabeledField
        id="dg-ot-mpsa"
        label={OT_LABEL}
        value={value.otMpsa}
        onChange={(v) => set("otMpsa", v)}
        disabled={disabled}
        placeholder="N° orden de trabajo"
        hint="Orden de trabajo (MPSA)."
      />
      <LabeledDate
        id="dg-emision"
        label="Emisión"
        value={value.emision}
        onChange={(v) => set("emision", v)}
        disabled={disabled}
        hint="Fecha de emisión del documento."
      />
      {/* Anexo = correlativo del informe (el "001" del número). Automático, no editable. */}
      <LabeledField
        id="dg-anexo"
        label="Anexo"
        value={value.anexo}
        onChange={(v) => set("anexo", v)}
        disabled
        placeholder="—"
        hint="Correlativo del informe (automático)."
      />
      <LabeledNumber
        id="dg-revision"
        label="Revisión"
        value={value.revision}
        onChange={(v) => set("revision", v)}
        disabled={disabled}
        hint="0 inicial; 1, 2, 3… si hay correcciones."
      />
    </div>
  );
}
